import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import Dashboard from "./pages/Dashboard";
import "./App.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <RegisterPage />
                </PublicRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <ForgotPasswordPage />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password"
              element={<ResetPasswordPage />}
            />

            {/* Protected Routes - Notes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard activeModule="my-notes" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/common-notes"
              element={
                <ProtectedRoute>
                  <Dashboard activeModule="common-notes" />
                </ProtectedRoute>
              }
            />

            {/* Protected Routes - To-Dos */}
            <Route
              path="/my-todos"
              element={
                <ProtectedRoute>
                  <Dashboard activeModule="my-todos" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/common-todos"
              element={
                <ProtectedRoute>
                  <Dashboard activeModule="common-todos" />
                </ProtectedRoute>
              }
            />

            {/* Protected Routes - Orders */}
            <Route
              path="/orders"
              element={
                <ProtectedRoute>
                  <Dashboard activeModule="orders" />
                </ProtectedRoute>
              }
            />

            {/* Protected Routes - File Manager */}
            <Route
              path="/files"
              element={
                <ProtectedRoute>
                  <Dashboard activeModule="files" />
                </ProtectedRoute>
              }
            />

            {/* Protected Routes - Recycle Bin */}
            <Route
              path="/recycle-bin"
              element={
                <ProtectedRoute>
                  <Dashboard activeModule="recycle-bin" />
                </ProtectedRoute>
              }
            />

            {/* Protected Routes - User Settings */}
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Dashboard activeModule="settings" />
                </ProtectedRoute>
              }
            />

            {/* Protected Routes - Admin */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <Dashboard activeModule="admin" />
                </ProtectedRoute>
              }
            />

            {/* Protected Routes - API Documentation */}
            <Route
              path="/api-docs"
              element={
                <ProtectedRoute>
                  <Dashboard activeModule="api-docs" />
                </ProtectedRoute>
              }
            />

            {/* Catch all - redirect to home */}
            <Route
              path="*"
              element={
                <ProtectedRoute>
                  <Dashboard activeModule="my-notes" />
                </ProtectedRoute>
              }
            />
          </Routes>
        </BrowserRouter>
        <Toaster position="bottom-right" />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
